import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { COLORWAY_STORAGE_KEY, ColorwayId, colorwayById, createDealSkoutTheme } from './createDealSkoutTheme';
import { THEME_STORAGE_KEY, ThemeModePreference } from './tokens';

type ThemeModeContextValue = {
  preference: ThemeModePreference;
  resolved: 'light' | 'dark';
  setPreference: (mode: ThemeModePreference) => void;
  colorway: ColorwayId;
  setColorway: (id: ColorwayId) => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

function readStoredPreference(): ThemeModePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemeModePreference>(() =>
    readStoredPreference()
  );
  const [systemDark, setSystemDark] = useState(() => systemPrefersDark());
  const [colorway, setColorwayState] = useState<ColorwayId>(() => {
    try {
      return colorwayById(localStorage.getItem(COLORWAY_STORAGE_KEY)).id;
    } catch {
      return 'default';
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const setPreference = useCallback((mode: ThemeModePreference) => {
    setPreferenceState(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const setColorway = useCallback((id: ColorwayId) => {
    setColorwayState(id);
    try {
      localStorage.setItem(COLORWAY_STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const resolved: 'light' | 'dark' =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  const theme = useMemo(() => createDealSkoutTheme(resolved, colorway), [resolved, colorway]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', colorwayById(colorway).navy);
  }, [colorway]);

  const value = useMemo(
    () => ({ preference, resolved, setPreference, colorway, setColorway }),
    [preference, resolved, setPreference, colorway, setColorway]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
}
